import React, { createContext, useContext } from 'react';
import { DEFAULT_LOCALE } from './constants';
import { buildLocalizedPath, normalizeLocale, persistLocale } from './locale';
import { TRANSLATIONS } from './translations';

const LocaleContext = createContext({
    locale: DEFAULT_LOCALE,
    setLocalePreference: () => {},
    t: (key) => key,
    toLocalizedPath: (routeKeyOrPath) => routeKeyOrPath
});

function getValueByPath(dictionary, key) {
    return key.split('.').reduce((value, segment) => {
        if (value && Object.prototype.hasOwnProperty.call(value, segment)) {
            return value[segment];
        }

        return undefined;
    }, dictionary);
}

function interpolate(template, variables = {}) {
    return Object.entries(variables).reduce((result, [key, value]) => {
        return result.replace(new RegExp(`{{\\s*${key}\\s*}}`, 'g'), String(value));
    }, template);
}

export function LocaleProvider({ children, locale }) {
    const normalizedLocale = normalizeLocale(locale);
    const localeMessages = TRANSLATIONS[normalizedLocale] || TRANSLATIONS[DEFAULT_LOCALE];

    function t(key, variables) {
        const message = getValueByPath(localeMessages, key)
            || getValueByPath(TRANSLATIONS[DEFAULT_LOCALE], key);

        if (typeof message !== 'string') {
            return key;
        }

        return interpolate(message, variables);
    }

    function setLocalePreference(nextLocale) {
        persistLocale(nextLocale);
    }

    function toLocalizedPath(routeKeyOrPath) {
        return buildLocalizedPath(normalizedLocale, routeKeyOrPath);
    }

    return (
        <LocaleContext.Provider
            value={{
                locale: normalizedLocale,
                setLocalePreference,
                t,
                toLocalizedPath
            }}
        >
            {children}
        </LocaleContext.Provider>
    );
}

export function useLocale() {
    return useContext(LocaleContext);
}